'use strict';

import {
  analyzeRegexPattern, wrapRuns, parseReplacement,
  regexExecAll, runReplace, replacementMarksOutput, regexError,
} from '../regex.js';
import { buildHelpHTML } from '../../core/util.js';
import { REGEX_CHEAT_LINK } from '../assist-regex.js';
import { matchModeOk, anchorPattern } from '../search.js';
import {
  MATCH_PARAM, matchModeOf, ALLOW_UNLISTED_PARAM, isReplacing,
  matchModeAssets, matchModePasses, matchModeSpacing, matchModeSpacingLazy,
} from './shared.js';

function build(params, spacing) {
  // Don't trim: in a regex a leading/trailing space is a literal that must
  // match. Re-adding `.trim()` reads as cleanup but silently drops it.
  const body = params.pattern;
  // Flags `gid`: `i` lets a raw (un-lowercased, so `\D \S \B` survive)
  // pattern match case-insensitively; `d` exposes match indices for
  // highlighting. The pattern runs against both norm and display (see run),
  // so `\s`, `-`, or an accent can match the punctuation display carries but
  // norm strips.
  const matchMode = matchModeOf(params);
  const wrap = src => anchorPattern(matchMode, src);
  const { capturing, runs } = analyzeRegexPattern(body);
  if (isReplacing(params)) {
    // The functional `re` can't be wrapped for highlighting — synthetic
    // groups would renumber the user's `$N`; `hlRe` is the wrapped copy.
    const hlRe = capturing ? null : new RegExp(wrap(wrapRuns(body, runs)), 'gid');
    return { mode: 'replace', re: new RegExp(wrap(body), 'gid'), hlRe, tokens: parseReplacement(params.replace), allowUnlisted: !!params['unlisted'], matchMode, spacing };
  }
  return { mode: 'filter', re: new RegExp(wrap(capturing ? body : wrapRuns(body, runs)), 'gid'), matchMode, spacing };
}

export default {
  name: 'Regex', icon: '🔮', category: 'search',
  desc: 'Search (and replace) with regular expressions',
  example: 'un.+ed · c.{2,4}t',
  findReplace: true,
  params: [
    { key: 'pattern', raw: true, placeholder: 'pattern', assist: 'regex' },
    { key: 'replace', placeholder: 'replace', raw: true, encodeEmpty: true, assist: 'regex-replace', help: buildHelpHTML([
      ['$1', 'first capture group'],
      ['$2', 'second group, etc.'],
      ['$&', 'the whole match'],
      ['$$', 'a literal $'],
    ], { cols: 1, link: REGEX_CHEAT_LINK }) },
    MATCH_PARAM,
    ALLOW_UNLISTED_PARAM,
  ],
  kind: params => (isReplacing(params) ? 'transform' : 'filter'),
  input: 'highlight',
  output: params => (replacementMarksOutput(parseReplacement(params.replace ?? ''), analyzeRegexPattern(params.pattern || '').capturing)
    ? 'highlight' : 'plain'),
  glyph: params => (isReplacing(params) ? '→' : null),
  // A half-typed, invalid pattern is inert like an empty one, so the view
  // neither blanks nor churns mid-keystroke.
  isInert(params) {
    const pattern = params && params.pattern || '';
    return !pattern || !!regexError(pattern);
  },
  error: params => regexError(params && params.pattern),
  matchOn: 'both',
  assets: matchModeAssets,
  passes: matchModePasses,
  async prepare(params, ctx) {
    return build(params, await matchModeSpacing(params, ctx));
  },
  replay: (params, ctx) => build(params, matchModeSpacingLazy(params, ctx)),
  run(wlEntry, prepared, wordlist) {
    if (prepared.mode === 'filter') {
      const { re, matchMode, spacing } = prepared;
      const d = wlEntry.display;
      const normRes = regexExecAll(re, wlEntry.norm, matchModeOk(matchMode, wlEntry, 'norm', spacing));
      const dispRes = d != null ? regexExecAll(re, d, matchModeOk(matchMode, wlEntry, 'display', spacing)) : null;
      if (!normRes.hit && !dispRes?.hit) return null;
      if (dispRes?.ranges.length) return dispRes.ranges.map(r => ({ ...r, coord: 'display' }));
      if (normRes.ranges.length) return normRes.ranges.map(r => ({ ...r, coord: 'norm' }));
      return true;
    }
    return runReplace(wlEntry, prepared, wordlist);
  },
};
